'use client';

import {
  ButtonLeadingIconSlot,
  ButtonSpinnerSlot,
  ButtonToggleIconSlot,
  ButtonTrailingIconSlot,
} from '@magicschool/spellbook/components/Button/slots';
import type { IconAnimateMode } from '@magicschool/spellbook/components/Icon';
import { Text } from '@magicschool/spellbook/components/Text';
import { useAnimatedIcon } from '@magicschool/spellbook/hooks/useAnimatedIcon';
import { useRenderedChildren } from '@magicschool/spellbook/hooks/useRenderedChildren';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getButtonTextVariant } from '@magicschool/spellbook/lib/controls';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui/slot';
import { memo } from 'react';

export type ButtonProps = React.ComponentProps<'button'> &
  Omit<VariantProps<typeof buttonVariants>, 'icon'> & {
    /** Whether the button is rendering as a different component. Renders button styling on child element (e.g., `Link`) — enables navigation with button appearance. */
    asChild?: boolean;
    /** If true, the button will display a loading spinner icon and click functionality will be disabled. */
    isLoading?: boolean;
    /** Animate the leading/trailing icon on hover and focus of the button. `'hover'` plays on hover or focus; `'mount'` plays on mount; `false` explicitly disables. Only valid for `AnimatedIconName` values. */
    animate?: IconAnimateMode;
  };

/**
 * @TODO [SPE-2] Consolidate button style classes
 * https://linear.app/magicschool/issue/SPE-2/consolidate-button-style-classes
 */
export const buttonVariants = cva(
  [
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:cursor-not-allowed',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:shadow-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:transition-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:[&_svg]:text-disabled-foreground',
    '[&_svg]:shrink-0',
    'aria-busy:pointer-events-none',
    'aria-invalid:ring-error-stroke',
    'border-1',
    'cursor-pointer',
    'focus-visible-ring',
    'group/button',
    'inline-flex',
    'items-center',
    'justify-center',
    'outline-none',
    'overflow-hidden',
    'shrink-0',
    'text-ellipsis',
    'transition-colors',
    'whitespace-nowrap',
  ],
  {
    variants: {
      /** The visual style of the button. */
      variant: {
        /** Solid filled button */
        solid: 'border-transparent [&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
        /** Muted filled button */
        muted: 'border-transparent [&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
        /** Button with border and transparent background */
        outlined: [
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-transparent',
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
          'active:bg-surface-background-3',
          'active:border-border-strong-hover',

          'bg-surface-background-0',
          'border-border-strong',
          'data-[state=open]:bg-surface-background-2',
          'data-[state=open]:border-border-strong-hover',
          'hover:bg-surface-background-2',
          'hover:border-border-strong-hover',
        ],
        /** Button with transparent background and no border */
        ghost: [
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-transparent',
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-transparent',
          'active:bg-surface-transparent-2',

          'bg-surface-transparent-0',
          'border-transparent',
          'data-[state=open]:bg-surface-transparent-2',
          'hover:bg-surface-transparent-1',
        ],
        /** Transparent background, default text, never a border, never an open state. Should be used exclusively for tooltips. */
        tooltip: [
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-transparent',
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-transparent',
          'bg-transparent',
          'border-transparent',
          'text-default-text',
          '[&_svg]:text-default-icon',
          'hover:[&_svg]:text-default-icon-hover',
          'focus-visible:!ring-offset-0',
        ],
      },
      /** The color theme of the button. */
      color: {
        /** Primary theme colors */
        primary: '',
        /** Error/destructive theme colors */
        error: 'focus-visible:!ring-error-stroke',
        /** Neutral theme colors */
        neutral: '',
        /** Disabled theme colors */
        disabled: [
          'text-disabled-foreground',
          'border-transparent',
          'shadow-none',
          'transition-none',
          '[&_svg]:text-disabled-foreground',
          'cursor-not-allowed',
          '[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-disabled-foreground',
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-transparent',
        ],
      },
      /** The size of the button. */
      size: {
        xs: ['h-control-h-xs', 'gap-0', 'px-1', 'py-1', 'min-w-7'],
        sm: ['h-control-h-sm', 'gap-0.5', 'px-1.5', 'py-1', 'min-w-7'],
        md: ['h-control-h-md', 'gap-1', 'px-2', 'py-2', 'min-w-14'],
        lg: ['h-control-h-lg', 'gap-1', 'px-3', 'py-3', 'min-w-20'],
      },
      /** Whether the button should take up the full width of its container. */
      fullWidth: {
        true: 'w-full',
      },
      /** The alignment of the button's content. */
      justify: {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        between: 'justify-between',
      },
      /** The radius shape of the button. */
      shape: {
        default: '', // handled in compoundVariants
        circle: 'rounded-full',
        square: 'rounded-none',
      },
      /** Whether the button is an `IconButton`. Passed internally when referencing `buttonVariants` - cannot be passed to a `Button` component. */
      icon: {
        true: 'min-w-0',
      },
      /** Whether the button is rendering as a different component. This is used internally to enforce default text styles without the expectation of a `Text` component for children. */
      asChild: {
        true: 'inline-flex',
      },
      /** If true, the button will display a loading spinner icon and click functionality will be disabled. */
      isLoading: {
        true: 'pointer-events-none opacity-90',
      },
      /** Internal toggle icon custom styling (see compoundVariants below). */
      hasToggleIcon: {
        true: 'min-w-0',
      },
    },
    compoundVariants: [
      {
        className: 'text-sm font-medium', // `label-1`
        asChild: true,
        size: ['lg', 'md', 'sm'],
      },
      {
        className: 'text-xs font-medium', // `label-3`
        asChild: true,
        size: 'xs',
      },
      {
        className: 'rounded-control-sm',
        size: ['xs', 'sm'],
        shape: 'default',
      },
      {
        className: 'rounded-control-md',
        size: 'md',
        shape: 'default',
      },
      {
        className: 'rounded-control-lg',
        size: 'lg',
        shape: 'default',
      },
      {
        className: ['bg-disabled-background', '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background'],
        variant: 'solid',
        color: 'disabled',
      },
      {
        className: ['hover:[&_svg]:text-disabled-foreground', 'hover:text-disabled-foreground'],
        variant: 'tooltip',
        color: 'disabled',
      },
      {
        className: [
          'bg-main-action-background',
          'text-main-action-foreground-text',
          '[&_svg]:text-main-action-foreground-icon',
          'hover:bg-main-action-background-hover',
          'active:bg-main-action-background-active',
          'data-[state=open]:bg-main-action-background-active',
        ],
        variant: 'solid',
        color: 'primary',
      },
      {
        className: [
          'bg-default-solid-background',
          'text-default-solid-foreground-text',
          '[&_svg]:text-default-solid-foreground-icon',
          'hover:bg-default-solid-background-hover',
          'active:bg-default-solid-background-active',
          'data-[state=open]:bg-default-solid-background-active',
        ],
        variant: 'solid',
        color: 'neutral',
      },
      {
        className: [
          'bg-error-solid-background',
          'text-error-solid-foreground-text',
          '[&_svg]:text-error-solid-foreground-icon',
          'hover:bg-error-solid-background-hover',
          'active:bg-error-solid-background-active',
          'data-[state=open]:bg-error-solid-background-active',
        ],
        variant: 'solid',
        color: 'error',
      },
      {
        className: [
          'bg-primary-muted-background',
          'hover:bg-primary-muted-background-hover',
          'active:bg-primary-muted-background-active',
          'data-[state=open]:bg-primary-muted-background-active',

          'text-link-default-default',
          '[&_svg]:text-primary-icon',
          'hover:text-link-default-hover',
          'active:text-link-default-active',
          'data-[state=open]:text-link-default-active',
        ],
        variant: 'muted',
        color: 'primary',
      },
      {
        className: [
          'bg-default-muted-background',
          'hover:bg-default-muted-background-hover',
          'active:bg-default-muted-background-active',
          'data-[state=open]:bg-default-muted-background-active',

          'text-default-text',
          '[&_svg]:text-default-icon',
          'hover:text-default-text-strong',
          'active:text-default-text-strong',
          'data-[state=open]:text-default-text-strong',
        ],
        variant: 'muted',
        color: 'neutral',
      },
      {
        className: [
          'bg-error-muted-background',
          'hover:bg-error-muted-background-hover',
          'active:bg-error-muted-background-active',
          'data-[state=open]:bg-error-muted-background-active',

          'text-error-text',
          '[&_svg]:text-error-icon',
          'hover:text-error-text-strong',
          'active:text-error-text-strong',
          'data-[state=open]:text-error-text-strong',
        ],
        variant: 'muted',
        color: 'error',
      },
      {
        className: [
          'text-link-default-default',
          '[&_svg]:text-primary-icon',
          'hover:text-link-default-hover',
          'active:text-link-default-active',
          'data-[state=open]:text-link-default-active',
        ],
        variant: 'ghost',
        color: 'primary',
      },
      {
        className: [
          'text-default-text',
          '[&_svg]:text-default-icon',
          'hover:text-default-text-strong',
          'active:text-default-text-strong',
          'data-[state=open]:text-default-text-strong',
        ],
        variant: 'ghost',
        color: 'neutral',
      },

      {
        className: [
          'text-default-text',
          '[&_svg]:text-default-icon',
          'hover:text-error-text',
          'hover:[&_svg]:text-error-icon',
          'focus-visible:ring-error-stroke',
          'active:text-error-text-strong',
          'data-[state=open]:text-error-text-strong',
        ],
        variant: 'ghost',
        color: 'error',
      },
      {
        className: [
          'text-link-default-default',
          '[&_svg]:text-primary-icon',
          'hover:text-link-default-hover',
          'active:text-link-default-active',
          'data-[state=open]:text-link-default-active',
        ],
        variant: 'outlined',
        color: 'primary',
      },
      // Neutral outlined variant
      {
        className: [
          'text-default-text',
          '[&_svg]:text-default-icon',
          'hover:text-default-text-strong',
          'active:text-default-text-strong',
          'data-[state=open]:text-default-text-strong',
        ],
        variant: 'outlined',
        color: 'neutral',
      },
      {
        className: [
          'text-default-text',
          '[&_svg]:text-default-icon',
          'hover:[&_svg]:text-error-icon',
          'hover:text-error-text',
          'focus-visible:ring-error-stroke',
          'active:text-error-text-strong',
          'data-[state=open]:text-error-text-strong',
        ],
        variant: 'outlined',
        color: 'error',
      },
      // Toggle icon styles for solid variants
      {
        className: [
          '[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-primary-solid-foreground-icon',
          'hover:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-primary-solid-foreground-icon',
          'active:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-primary-solid-foreground-icon',
          'data-[state=open]:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-primary-solid-foreground-icon',
        ],
        hasToggleIcon: true,
        variant: 'solid',
        color: 'primary',
      },
      {
        className: [
          '[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-error-solid-foreground-icon',
          'hover:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-error-solid-foreground-icon',
          'active:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-error-solid-foreground-icon',
          'data-[state=open]:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-error-solid-foreground-icon',
        ],
        hasToggleIcon: true,
        variant: 'solid',
        color: 'error',
      },
      // Toggle icon styles for outlined, ghost, and muted variants
      {
        className: [
          '[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-default-icon',
          'hover:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-default-icon-hover',
          'active:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-default-icon-hover',
          'data-[state=open]:[&_:is([data-spellbook-name=button-toggle-icon],[data-spellbook-name=icon-button-toggle-icon])]:text-default-icon-hover',
        ],
        hasToggleIcon: true,
        variant: ['outlined', 'ghost', 'muted'],
      },
      {
        className: 'justify-center',
        fullWidth: false,
        justify: ['start', 'end', 'between'],
      },
      {
        icon: true,
        hasToggleIcon: false,
        className: 'aspect-square p-0',
      },
    ],
  },
);

type ButtonSlottedContentProps = {
  slots: {
    loadingIcon?: React.ReactNode;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    toggleIcon?: React.ReactNode;
  };
  rest?: React.ReactNode;
  size?: ButtonProps['size'];
  isLoading?: boolean;
};

const ButtonSlottedContent = memo<ButtonSlottedContentProps>(({ slots, size, rest, isLoading }) => {
  const textVariant = getButtonTextVariant(size);
  return (
    <>
      {isLoading ? slots.loadingIcon : slots.leadingIcon}
      {rest && (
        <Text variant={textVariant} className="px-1 min-w-0 max-w-full truncate" element="span">
          {rest}
        </Text>
      )}
      {slots.trailingIcon}
      {slots.toggleIcon}
    </>
  );
});

const ButtonBase: React.FC<ButtonProps> = ({
  animate,
  asChild,
  children,
  className,
  disabled,
  fullWidth = false,
  isLoading = false,
  onClick,
  justify = 'center',
  shape = 'default',
  size = 'md',
  variant = 'solid',
  color = 'primary',
  type = 'button',
  ...props
}) => {
  const Comp = asChild ? Slot : 'button';
  const isActuallyDisabled = disabled || isLoading;
  const resolvedAnimate = isActuallyDisabled ? undefined : animate;
  const { animating, getInteractProps } = useAnimatedIcon(resolvedAnimate);

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isActuallyDisabled) {
      // explicitly prevent default and stop propagation so that `asChild` components are also disabled
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    onClick?.(event);
  };

  const iconSlotProps = { size, animate: resolvedAnimate, animating };

  const { slots, rest } = useSlots(children, {
    loadingIcon: { Component: ButtonSpinnerSlot, props: { size } },
    leadingIcon: { Component: ButtonLeadingIconSlot, props: iconSlotProps },
    trailingIcon: { Component: ButtonTrailingIconSlot, props: iconSlotProps },
    toggleIcon: { Component: ButtonToggleIconSlot, props: { size } },
  });

  const { renderedChildren } = useRenderedChildren(children, {
    slots: {
      loadingIcon: { Component: ButtonSpinnerSlot, props: { size } },
      leadingIcon: { Component: ButtonLeadingIconSlot, props: iconSlotProps },
      trailingIcon: { Component: ButtonTrailingIconSlot, props: iconSlotProps },
      toggleIcon: { Component: ButtonToggleIconSlot, props: { size } },
    },
    renderContent: ({ slots, rest }) => <ButtonSlottedContent slots={slots} rest={rest} size={size} isLoading={isLoading} />,
  });

  return (
    <Comp
      aria-disabled={isActuallyDisabled}
      aria-busy={isLoading}
      data-spellbook-name="button"
      data-border={variant === 'outlined'}
      className={cn(
        buttonVariants({
          variant,
          color,
          size,
          fullWidth,
          justify,
          shape,
          asChild,
          isLoading,
          hasToggleIcon: Boolean(slots.toggleIcon),
        }),
        className,
      )}
      type={Comp === 'button' ? type : undefined}
      {...props}
      {...getInteractProps(props)}
      onClick={handleOnClick}
    >
      {asChild ? renderedChildren : <ButtonSlottedContent slots={slots} rest={rest} size={size} isLoading={isLoading} />}
    </Comp>
  );
};

/**
 * Primary interactive element for triggering actions. Supports multiple visual styles, icon slots, loading states, and polymorphic rendering via `asChild` for navigation links.
 *
 * ### When to use
 * - For actions that trigger immediate behavior (submit form, open modal, delete item)
 * - For navigation when button styling is required — use `asChild` with `Link` component
 *
 * ### When NOT to use
 * - For navigation with link styling: use `Link` component instead
 * - For icon-only actions: use `IconButton` component instead
 * - For toggle switches: use `Switch`, `ToggleIconButton`, or `ToggleButton` components instead
 *
 * ### API
 * - `variant`: Visual style of the button:
 *   - `solid`: Filled background (default) — use for primary/emphasized actions
 *   - `muted`: Tinted/muted filled background — use for soft actions that need more visual weight than `ghost` but less prominence than `solid`; pairs with `primary`, `neutral`, or `error` color
 *   - `outlined`: Border with surface background — use for secondary actions
 *   - `ghost`: No border or background — use for tertiary/subtle actions
 *   - `tooltip`: Special variant for tooltip triggers only — never use for general buttons
 * - `color`: Semantic color theme:
 *   - `primary`: Brand color (default) — use for main actions
 *   - `neutral`: Muted color — use for less prominent actions
 *   - `error`: Destructive color — use for delete, remove, or destructive actions
 * - `size`: Button dimensions: `xs`, `sm`, `md` (default), `lg`
 * - `isLoading`: Shows spinner, disables clicks, and sets `aria-busy="true"`
 * - `asChild`: Renders button styling on child element (e.g., `Link`) — enables navigation with button appearance
 * - `fullWidth`: Expands button to fill container width
 * - `justify`: Content alignment: `start`, `center` (default), `end`, `between`. Only applies for buttons with a listed hierarchy of content (such as left navigation)
 * - `shape`: Border radius: `default` (rounded), `circle` (pill), `square` (none). `circle` is **not** intended for use with `Button` — use `IconButton` instead for icon-only circular buttons. `square` is available for edge cases that require sharp corners, but use with caution as it can create a jarring visual experience if not used intentionally.
 *
 * ### Composition (slots)
 * - `<Button.LeadingIcon>`: Icon before text. Pass `name` prop with a Lucide icon name (e.g., `<Button.LeadingIcon name="arrow-left" />`). See https://lucide.dev for available icons. Size is handled automatically. Only include when adding an icon — not rendered by default. Use `overrideDefault` for non-icon content like avatars.
 * - `<Button.TrailingIcon>`: Icon after text. Pass `name` prop with a Lucide icon name. See https://lucide.dev for available icons. Size is handled automatically. Only include when adding an icon — not rendered by default. Use `overrideDefault` for non-icon content like avatars.
 * - `<Button.ToggleIcon>`: Chevron that rotates on open/closed state. Include `<Button.ToggleIcon />` with no props — only needed when using dropdown/collapsible behavior. Rotation is automatic based on `data-state="open"` or `aria-expanded="true"`.
 * - `<Button.Spinner>`: Loading indicator shown when `isLoading={true}`. Replaces `LeadingIcon` during loading. Rendered automatically when `isLoading={true}` — no need to include explicitly.
 *
 * ### Visual states
 * - **Hover**: Background color shifts based on variant — `solid` lightens/darkens fill, `outlined`/`ghost` add subtle background
 * - **Active/Pressed**: Deeper background shift to indicate interaction
 * - **Loading**: Spinner replaces leading icon, pointer events blocked, `aria-busy="true"` set
 * - **Disabled**: Text and icons use disabled color, background becomes transparent (except `solid`), cursor shows not-allowed, `aria-disabled="true"` set
 * - **Focus**: Focus ring appears around button for keyboard navigation
 * - **Open (dropdown/collapsible)**: Same as active state; `ToggleIcon` rotates 180° when `data-state="open"` or `aria-expanded="true"`
 *
 * ### Accessibility
 * - Uses `aria-disabled` instead of `disabled` attribute to maintain focusability for screen readers. Pass `disabled` prop and the component will handle setting `aria-disabled` and appropriate styles.
 * - Sets `aria-busy="true"` during loading state to announce to assistive technology. Pass `isLoading` prop to enable loading state and the component will handle ARIA attributes and styles.
 * - Click handlers are blocked when disabled or loading, including for `asChild` components
 * - Always include visible text labels for buttons to ensure accessibility. If an action cannot have a visible label, use `IconButton` with an accessible `label` prop instead.
 * - Keyboard: Enter/Space activates the button; focus is visible via ring outline
 *
 * ### `asChild` pattern
 * ```tsx
 * <Button asChild>
 *   <Link href="/dashboard">Go to Dashboard</Link>
 * </Button>
 * ```
 * Slot components (`LeadingIcon`, `TrailingIcon`) still work when using `asChild`:
 * ```tsx
 * <Button asChild variant="outlined">
 *  <Link href="/settings">
 *    <Button.LeadingIcon name="settings" />
 *    Settings
 *  </Link>
 * </Button>
 * ```
 *
 * ### Choosing the right button component
 * | Component | Use when | Example |
 * |-----------|----------|---------|
 * | `Button` | Action has visible text label | "Save", "Submit", "Cancel" |
 * | `IconButton` | Icon-only action in compact UI | Toolbar close button, settings gear |
 * | `ToggleButton` | Text button toggles on/off state | "Bold" formatting button with text |
 * | `ToggleIconButton` | Icon-only button toggles on/off | Favorite heart, bookmark star |
 *
 * **Why separate components?**
 * - `IconButton` enforces `label` prop for accessibility — icon-only buttons need screen reader text
 * - `ToggleButton`/`ToggleIconButton` use Radix Toggle primitive with `pressed` state and `aria-pressed`
 * - Separating concerns ensures correct accessibility patterns are enforced by the API
 *
 * ### Additional notes
 * - Loading state replaces `LeadingIcon` with `Spinner` and blocks all click interactions
 * - `ToggleIcon` automatically rotates 180° when button has `data-state="open"` or `aria-expanded="true"`
 * - Use `color="error"` with `variant="solid"` for destructive actions only
 * - For Spellbook contributors: `buttonVariants` can be used internally to apply button styling to custom components, and is used on `IconButton`, `ToggleButton`, and `ToggleIconButton` for consistent style reference.
 */
export const Button = Object.assign(ButtonBase, {
  LeadingIcon: ButtonLeadingIconSlot,
  TrailingIcon: ButtonTrailingIconSlot,
  ToggleIcon: ButtonToggleIconSlot,
  Spinner: ButtonSpinnerSlot,
});
